import React, { FC } from 'react';
import Modal from 'components/shared/Modal';
import EditDateTimeForm from 'components/shared/EditDateTimeForm';
import { useMutation } from 'react-query';
import PostApi from 'api/post';
import { formatTime } from 'app/utils/date';
import { useToast } from 'app/hooks/useToast';
import { usePostOverviewOptimisticQueryUpdater } from 'app/modules/posts/modules/post/hooks/usePostOverview';
import Logger from 'utils/logger';
import { PostDetail } from 'types/PostDetail';
import { ApiClientError } from 'api/client';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { canSaveScheduledDateTime } from 'app/modules/posts/utils/scheduledPastDate';

type Props = {
  open: boolean;
  onClose: () => void;
  post: Pick<PostDetail, 'id' | 'date' | 'time' | 'page'> & {
    status: PostDetail['status'] | number;
  };
  onSave?: (values: { date: string; time: string | null }) => void;
};

const QuickPostEditDateTime: FC<Props> = ({ open, onClose, post, onSave }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const updater = usePostOverviewOptimisticQueryUpdater(post.id);
  const updatePost = useMutation<
    PostDetail,
    ApiClientError,
    {
      date: Dayjs;
      time: string | null;
    }
  >(
    (variables) =>
      PostApi.savePartial({
        postId: post.id,
        values: variables,
      }),
    {
      onError(e) {
        Logger.error(e);
        toast(t('editDateTime.errorMessage'));
      },
      async onSuccess(_, variables) {
        const date = variables.date.format('YYYY-MM-DD');

        updater.update(() => ({
          date,
          time: variables.time,
        }));
        onSave?.({ date, time: variables.time });
        toast(t('editDateTime.successMessage'));
        onClose();
      },
      onSettled() {
        updater.invalidateData();
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      focusTrapped={false}
      isLoading={updatePost.isLoading}
      isContentHeightAutoResizable={false}
    >
      <Modal.Header title={t('editDateTime.title')} />
      <EditDateTimeForm
        initialDate={post.date}
        initialTime={post.time ? formatTime(post.time, 'HH:mm') : null}
        onSubmit={async (fields) => {
          const date = dayjs(fields.date);

          const canSave = await canSaveScheduledDateTime(
            {
              status: post.status,
              date,
              time: fields.time,
              pageTimezone: post.page?.timezone,
            },
            t,
          );

          if (!canSave) {
            return;
          }

          updatePost.mutate({ date, time: fields.time });
        }}
      />
    </Modal>
  );
};

export default QuickPostEditDateTime;
